// When you dispatch, state.changes is populated so we wait until that is done before saving.

import React, { useEffect, useMemo } from 'react'
import { InfoSheetPanel, Button, DataBlock, Separator, Row } from '@fs/zion-ui'
import { FormAutosuggest, useZionFormContext } from '@fs/zion-form'
import { useTranslation } from 'react-i18next'
import zionDebug from '@fs/zion-debug'
import { useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import PanelHeader from '../shared/PanelHeader'
import { getRecordTypesForAutosuggest } from '../../../../../shared/utils/utils'
import HelpOverlayLearnMore from '../../../sharedHelp/HelpOverlayLearnMore'

const debug = zionDebug('rip:R2:AddPerson')
export default function AddRecordType({ selectedPersonId, saving, handleBack, submitFunc }) {
  const { addPersonPage2GenericOverlay, editHighlightsOverlay, topMostPanel, closeOverlay } = useOverlayContext()
  const helpActionUrl = 'https://www.familysearch.org/en/help/helpcenter/article/what-is-a-record-type'
  const state = useSlsStateContext()
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const { getValues, handleSubmit } = useZionFormContext()
  const [givenField, surnameField] = getValues(['givenField', 'surnameField'])

  const personName =
    `${givenField || ''} ${surnameField || ''}`.trim() || t('get-person-full-name.unknown', '<Unknown>')

  const userClosedPanel = () => {
    closeOverlay('addPersonPage2Generic')
    handleBack()
  }

  useEffect(() => {
    if (
      addPersonPage2GenericOverlay?.isOpen &&
      topMostPanel() === 'addPersonPage2Generic' &&
      !editHighlightsOverlay.isOpen
    ) {
      h.setDisplayHighlights({
        mainPartitionKey: state.currentImageArk,
        highlights: h
          .getHighlights()
          .mainEditHighlights.filter((i) => ['NAME_SURN', 'NAME_SUF', 'NAME_GN'].includes(i.fieldType))
          .filter((hi) => hi.personIds.length > 0)
          .filter((hi) => hi?.imageId === state.currentImageArk)
          .filter((hi) => hi?.personIds.includes(selectedPersonId)),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addPersonPage2GenericOverlay?.isOpen,
    editHighlightsOverlay.isOpen,
    selectedPersonId,
    state.currentImageArk,
    topMostPanel,
  ])

  const recordTypeSuggestions = useMemo(() => {
    return getRecordTypesForAutosuggest(state.types, state?.images, state?.currentImageArk, t)
  }, [state?.currentImageArk, state?.images, state.types, t])

  debug('Render: ', { state })

  return (
    <InfoSheetPanel
      {...addPersonPage2GenericOverlay}
      close={userClosedPanel}
      headingColor="purple"
      title={t('add-person.panel-back', 'Back')}
      footer={
        <Row alignX="end" gutters="md">
          <Button
            linkName="Add Person Back"
            data-testid="panel-back-button"
            emphasis="low"
            type="submit"
            form=""
            onClick={() => {
              closeOverlay('addPersonPage2Generic')
              handleBack()
            }}
            disabled={saving}
          >
            {t('edit-shared-surname-footer.back', 'Back')}
          </Button>
          <Button
            linkName="Add Person Save"
            data-testid="panel-forward-button"
            emphasis="high"
            disabled={saving}
            form="add-person-form-page-2"
            onClick={() => {
              handleSubmit(submitFunc)()
            }}
          >
            {t('edit-shared-surname-footer.save', 'Save')}
          </Button>
        </Row>
      }
    >
      <PanelHeader
        heading={t('add-person.panel-heading', 'Add Name')}
        caption={t('add-person.page-2-caption', 'Select a record type for the new name.')}
      />
      <DataBlock
        heading=""
        data={t(
          'add-person.page-2-subtitle',
          'What is the record type? If you are unsure of what to enter, leave it blank.',
          {
            name: personName,
          }
        )}
      />
      <Separator size="xs" />
      <FormAutosuggest
        data-testid="recordTypeAutosuggest"
        name="recordType"
        label={t('add-person.new-record-type-prompt', 'Record Type')}
        placeholder={t('add-person.new-record-type-prompt', 'Record Type')}
        clearOnClickAway
        suggestions={recordTypeSuggestions}
        helpMessage={
          <>
            {t('add-person.what-are-record-types', 'What are record types? ')}
            <HelpOverlayLearnMore helpActionUrl={helpActionUrl} />
          </>
        }
        sortFunction={(inputText, suggestionA, suggestionB) => {
          // prioritize exact matches over alphabetically preceding partial matches
          const normalizedInputText = inputText.toLowerCase().trim()
          if (suggestionA.primaryText.toLowerCase().startsWith(normalizedInputText)) return -1
          if (suggestionB.primaryText.toLowerCase().startsWith(normalizedInputText)) return 1
          return suggestionA.primaryText - suggestionB.primaryText
        }}
      />
    </InfoSheetPanel>
  )
}
